import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  setIntakeForms,
  setIntakeForm,
  setLoading,
  setError,
} from "../slices/intakeSlice";
import { generateConfig } from "../../utils/api";

// Retrieve all intake forms
export const fetchIntakeForms = createAsyncThunk(
  "intake/fetchIntakeForms",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/intake`, config);
      dispatch(setIntakeForms(response.data));
      return response.data;
    } catch (error) {
      console.error("API Error in thunk:", error);
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

// Retrieve an existing intake form
export const fetchIntakeFormById = createAsyncThunk(
  "intake/fetchIntakeForm",
  async (id, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/intake/${id}`, config);
      dispatch(setIntakeForm(response.data));
      return response.data;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

// Create a new intake form
export const createIntakeForm = createAsyncThunk(
  "intakes/createIntakeForm",
  async (formData, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token; // If needed for authorization
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/intake`,
        formData,
        config
      );

      console.log("Response data from API:", response.data); // Debugging
      return response.data;
    } catch (error) {
      console.error("Error in createIntakeForm thunk:", error);
      return rejectWithValue(
        error.response?.data || "Error creating intake form"
      );
    }
  }
);

// Update an existing intake form
export const updateIntakeForm = createAsyncThunk(
  "intake/updateIntakeForm",
  async ({ id, updatedData }, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.patch(
        `${baseURL}/api/intake/${id}`,
        updatedData,
        config
      );
      dispatch(setIntakeForm(response.data));
      return response.data;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

// Update Intake Status
// Update Intake Status Thunk
export const updateIntakeStatus = createAsyncThunk(
  "intake/updateIntakeStatus",
  async ({ id, status, userId }, { dispatch, getState, rejectWithValue }) => {
    try {
      console.log("Making API call to update intake status...");

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.patch(
        `${baseURL}/api/intake/${id}/status`, // Ensure this matches your backend
        { status, userId },
        config
      );

      console.log("API Response:", response.data);

      return response.data;
    } catch (error) {
      console.error("Error updating intake status:", error);
      return rejectWithValue(
        error.response?.data || "Error updating intake status"
      );
    }
  }
);

// Delete an existing intake form
export const deleteIntakeForm = createAsyncThunk(
  "intake/deleteIntakeForm",
  async (id, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      await axios.delete(`${baseURL}/api/intake/${id}`, config);
      dispatch(fetchIntakeForms()); // Refresh the list after deletion
      return id;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

// Send intake forms email
export const sendIntakeFormsEmail = createAsyncThunk(
  "intake/sendIntakeFormsEmail",
  async ({ id, data }, { dispatch, getState, rejectWithValue }) => {
    console.log("[sendIntakeFormsEmail] id", id);
    try {
      dispatch(setLoading(true));
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/intake/${id}/email`,
        data,
        config
      );

      dispatch(setIntakeForm(response.data.intake));
      return response.data;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(
        error.response?.data || "Error sending intake form email"
      );
    }
  }
);

// Send completion notification email
export const sendCompletionNotification = createAsyncThunk(
  "intake/sendCompletionNotification",
  async ({ id, clientName, notificationEmail }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/intake/${id}/completion-notification`,
        { clientName, notificationEmail },
        config
      );

      return response.data;
    } catch (error) {
      console.error("Error sending completion notification:", error);
      return rejectWithValue(
        error.response?.data || "Error sending completion notification"
      );
    }
  }
);

// Add this new thunk
export const linkIntakeToClient = createAsyncThunk(
  "intake/linkToClient",
  async ({ intakeId, clientId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.patch(
        `${baseURL}/api/intake/${intakeId}/link-client`,
        { clientId },
        config
      );
      
      return response.data;
    } catch (error) {
      console.error("Error linking intake to client:", error);
      return rejectWithValue(error.response?.data || "Error linking intake to client");
    }
  }
);

// Add this new thunk
export const fetchIntakesByClientId = createAsyncThunk(
  "intake/fetchIntakesByClientId",
  async (clientId, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/intake/client/${clientId}`, config);
      return response.data;
    } catch (error) {
      console.error("Error fetching intakes by client:", error);
      return rejectWithValue(error.response?.data || "Error fetching intakes by client");
    }
  }
);

// Add this new thunk
export const createClientFromIntake = createAsyncThunk(
  "intake/createClientFromIntake",
  async ({ intakeId, createParents }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/intake/${intakeId}/create-client`,
        { createParents },
        config
      );
      
      return response.data;
    } catch (error) {
      console.error("Error creating client from intake:", error);
      return rejectWithValue(error.response?.data || "Error creating client from intake");
    }
  }
);
